import React, { useEffect } from "react";
import Nav from "../components/Nav";
import SEO, { generateWebPageSchema } from "../components/SEO";
import {
  getArticleCategoryUrl,
  getResourceCategoryUrl,
} from "../utils/seoPaths";

const Contact = () => {
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.prerenderReady = true;
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-white font-serif">
      <SEO
        title="Contact Recovery Compass NH - Submit Resources & Get Support"
        description="Contact Recovery Compass NH to submit new recovery resources, ask questions, or get help finding treatment centers and support services across New Hampshire. Email: recoverycompassnh@gmail.com"
        canonicalUrl="https://www.recoverycompassnh.com/contact"
        ogType="website"
        ogImage="https://www.recoverycompassnh.com/og-default.png"
        ogImageAlt="Contact Recovery Compass NH for recovery resource support"
        breadcrumbs={[
          { name: "Home", url: "https://www.recoverycompassnh.com/" },
          {
            name: "Contact",
            url: "https://www.recoverycompassnh.com/contact",
          },
        ]}
        schema={generateWebPageSchema({
          title: "Contact Recovery Compass NH",
          description:
            "Contact Recovery Compass NH to submit resources or get help finding recovery support services",
          url: "https://www.recoverycompassnh.com/contact",
        })}
      />

      <Nav />

      {/* Header Section */}
      <div className="bg-gray-800 border-b-2 border-cyan-500">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-white mb-4">CONTACT US</h1>
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="h-px bg-cyan-500 w-32"></div>
              <span className="text-cyan-400 font-semibold tracking-widest">
                GET IN TOUCH
              </span>
              <div className="h-px bg-cyan-500 w-32"></div>
            </div>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              Have a resource to share or a question about recovery services in
              New Hampshire? We'd love to hear from you.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="bg-gray-800 border border-gray-600 p-8">
            <h2 className="text-3xl font-bold text-cyan-400 mb-6 border-b-2 border-cyan-500 pb-2">
              REACH OUT DIRECTLY
            </h2>

            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-gray-900"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Email Us</h3>
                  <a
                    href="mailto:recoverycompassnh@gmail.com"
                    className="text-cyan-400 hover:text-cyan-300 transition-colors text-lg"
                  >
                    recoverycompassnh@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-gray-900"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Location</h3>
                  <p className="text-gray-300 text-lg">
                    Laconia, New Hampshire
                  </p>
                </div>
              </div>

              <div className="bg-gray-700 border border-gray-600 p-6 mt-8">
                <h4 className="text-lg font-bold text-cyan-400 mb-3">
                  Response Time
                </h4>
                <p className="text-gray-300">
                  We typically respond to all inquiries within 24-48 hours. For
                  urgent matters or crisis situations, please contact your local
                  emergency services or crisis hotline.
                </p>
              </div>
            </div>
          </div>

          {/* What We're Looking For */}
          <div className="bg-gray-800 border border-gray-600 p-8">
            <h2 className="text-3xl font-bold text-cyan-400 mb-6 border-b-2 border-cyan-500 pb-2">
              SUBMIT A RESOURCE
            </h2>

            <p className="text-gray-300 mb-6 text-lg leading-relaxed">
              Know of a recovery resource that should be listed on our site?
              Send us the details and we'll review it for inclusion in our
              directory.
            </p>

            <div className="bg-gray-700 border border-gray-600 p-6 mb-6">
              <h4 className="text-lg font-bold text-cyan-400 mb-3">
                What We're Looking For
              </h4>
              <ul className="text-gray-300 space-y-2">
                <li>• New treatment centers and programs</li>
                <li>• Support groups and meeting locations</li>
                <li>• Educational resources and workshops</li>
                <li>• Success stories and testimonials</li>
                <li>• Healthcare professionals and services</li>
              </ul>
            </div>

            <a
              href="mailto:recoverycompassnh@gmail.com?subject=Resource Submission"
              className="block w-full bg-cyan-500 text-gray-900 py-3 px-6 font-semibold hover:bg-cyan-400 transition-colors text-center"
            >
              EMAIL US YOUR RESOURCE
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-600 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-6">
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">CONTACT US</h5>
              <p className="text-sm text-gray-300">
                <br />
                Laconia, NH 03246
                <br />
              </p>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">SECTIONS</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href={getArticleCategoryUrl("unsung-heroes")}
                    className="hover:text-cyan-400"
                  >
                    Healthcare Heroes
                  </a>
                </li>
                <li>
                  <a
                    href={getArticleCategoryUrl("stories-of-recovery")}
                    className="hover:text-cyan-400"
                  >
                    Recovery Stories
                  </a>
                </li>
                <li>
                  <a href="/resources" className="hover:text-cyan-400">
                    Resources
                  </a>
                </li>
                <li>
                  <a
                    href={getArticleCategoryUrl("blog-posts")}
                    className="hover:text-cyan-400"
                  >
                    Blog
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">RESOURCES</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>
                  <a
                    href={getResourceCategoryUrl("Primary SUD Providers")}
                    className="hover:text-cyan-400"
                  >
                    Treatment Centers
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl(
                      "Online Resources and Support Groups"
                    )}
                    className="hover:text-cyan-400"
                  >
                    Support Groups
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl("Crisis Intervention Services")}
                    className="hover:text-cyan-400"
                  >
                    Crisis Hotline
                  </a>
                </li>
                <li>
                  <a
                    href={getResourceCategoryUrl(
                      "Primary Mental Health Providers"
                    )}
                    className="hover:text-cyan-400"
                  >
                    Mental Health
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold mb-3 text-cyan-400">FOLLOW US</h5>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61559004021648"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded flex items-center justify-center transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4 text-center">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Recovery Compass NH. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
